import React, { useRef, useEffect } from 'react';
import { useAnimationContext } from '@context/AnimationContext';
import { cn } from '@utils/cn';

/**
 * ScrollProgressProps
 * Props for the ScrollProgress component
 */
interface ScrollProgressProps {
  /** Additional CSS classes */
  className?: string;
  /** Height of the progress bar */
  height?: number;
  /** Color of the progress bar */
  color?: string;
  /** Position of the progress bar */
  position?: 'top' | 'bottom';
}

/**
 * ScrollProgress
 * Global page scroll progress indicator
 * Shows visual feedback of scroll position through the page
 *
 * @example
 * <ScrollProgress />
 */
const ScrollProgress: React.FC<ScrollProgressProps> = ({
  className = '',
  height = 4,
  color = 'bg-accent',
  position = 'top',
}) => {
  const { animationsEnabled } = useAnimationContext();
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!animationsEnabled) return;

    const updateProgress = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = docHeight > 0 ? scrollTop / docHeight : 0;
      if (barRef.current) {
        barRef.current.style.transform = `scaleX(${progress})`;
      }
    };

    window.addEventListener('scroll', updateProgress, { passive: true });
    updateProgress();
    return () => window.removeEventListener('scroll', updateProgress);
  }, [animationsEnabled]);

  if (!animationsEnabled) return null;

  return (
    <div
      ref={barRef}
      className={cn(
        'fixed left-0 right-0 z-[9999]',
        position === 'top' ? 'top-0' : 'bottom-0',
        color,
        className
      )}
      style={{
        height: `${height}px`,
        transformOrigin: 'left',
        transform: 'scaleX(0)',
      }}
      aria-hidden="true"
    />
  );
};

export default ScrollProgress;
